/**
 * setAuthToken — the single chokepoint for persisting a freshly minted cloud
 * JWT AND handing it off to the local sidecar.
 *
 * Auth now goes DIRECT to the cloud (the sidecar auth proxy is dead). But the
 * sidecar still needs the JWT to make its OWN authed cloud calls (module
 * downloads, license checks). So after ANY successful login — email/password,
 * register, or OAuth — we:
 *
 *   1. Write the token under 'gdb_token' (the localStorage key api-client reads
 *      as its Bearer header), then
 *   2. Fire-and-forget POST { token, rememberMe } to the LOCAL sidecar's
 *      `/api/auth/token` endpoint (reached via apiUrl, NOT the cloud), so the
 *      sidecar stores it for its own downloads.
 *
 * The hand-off is BEST-EFFORT and must never block or break web login:
 *   - In pure-website mode there is no sidecar — the POST fails (connection
 *     refused / 404) and we just log it. Web login still works because the
 *     token is already in localStorage and auth itself went to the cloud.
 *   - In plugin cloud-SpaSource mode the sidecar is present and accepts the
 *     hand-off so its downloads are authed.
 *
 * This is the ONE place that writes 'gdb_token' for the login/register/OAuth
 * success paths, replacing the bare `localStorage.setItem` calls those paths
 * used to do — so the sidecar hand-off can never be forgotten on a new path.
 */

import { apiUrl } from '@/lib/api/apiUrl'

/** localStorage key for the Bearer JWT. Must match lib/api-client.ts. */
export const TOKEN_KEY = 'gdb_token'

/** Sidecar endpoint that accepts the cloud JWT hand-off. */
const SIDECAR_TOKEN_PATH = '/api/auth/token'

/**
 * Persist the cloud JWT locally and hand it to the sidecar.
 *
 * @param token       The cloud-minted JWT to persist + hand off.
 * @param rememberMe  Whether the sidecar should persist the session across
 *                    restarts (mirrors the login `rememberMe`). Defaults to
 *                    true — the typical user wants to stay signed in.
 */
export function setAuthToken(token: string, rememberMe: boolean = true): void {
  // 1. Local persistence — the api-client's Bearer cache. Synchronous so any
  //    immediately-following authed call already sees the token.
  localStorage.setItem(TOKEN_KEY, token)

  // 2. Sidecar hand-off — fire-and-forget. Never await, never throw; a missing
  //    or erroring sidecar (pure-website mode) must NOT break login.
  void handOffToSidecar(token, rememberMe)
}

/**
 * POST the token to the local sidecar. Resolves silently on success; logs and
 * swallows on any failure (sidecar absent / offline / rejecting). Separated out
 * so the synchronous setAuthToken can `void` it without an inline async IIFE.
 */
async function handOffToSidecar(token: string, rememberMe: boolean): Promise<void> {
  try {
    const response = await fetch(apiUrl(SIDECAR_TOKEN_PATH), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, rememberMe }),
    })
    if (!response.ok) {
      console.warn(
        `[auth] sidecar token hand-off rejected (status ${response.status}) — ` +
          'continuing; sidecar downloads may be unauthed',
      )
      return
    }
    console.log('[auth] cloud JWT handed off to sidecar OK')
  } catch (error: unknown) {
    // Expected in pure-website mode (no sidecar). Best-effort only.
    const message = error instanceof Error ? error.message : String(error)
    console.warn(
      `[auth] sidecar token hand-off failed (sidecar likely absent): ${message}`,
    )
  }
}
